import React, { useState } from "react";
import { useModerationQueue } from "hooks/useModeration";

import Pagination from "views/pages/components/Pagination";
import { Avatar } from "@mui/material";
import constants from "constants";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import SimpleHeader from "components/Headers/SimpleHeader";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Input,
  Button,
  Badge,
  Spinner,
  Alert,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import useNotificationHighlight from "hooks/useNotificationHighlight";

const formatContext = (context, t) => {
  if (!context) return "";
  
  // Case 1: Dot nested key (e.g., contentSafety.illegalSubstances)
  if (context.includes(".") && !context.includes(",")) {
    let translationKey = `page.userListings.rejectionReasons.${context}.title`;
    let translated = t(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }
    translationKey = `page.users.details.rejectionReasons.${context}.title`;
    translated = t(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }
  }
  
  // Case 2: Comma separated errors (e.g., name_not_detected,date_of_birth_not_detected)
  if (context.includes(",")) {
    return context
      .split(",")
      .map(token => {
        const cleanToken = token.trim();
        
        // If the token has a dot-separated path
        if (cleanToken.includes(".")) {
          let transKey = `page.userListings.rejectionReasons.${cleanToken}.title`;
          let translated = t(transKey);
          if (translated && translated !== transKey) {
            return translated;
          }
          transKey = `page.users.details.rejectionReasons.${cleanToken}.title`;
          translated = t(transKey);
          if (translated && translated !== transKey) {
            return translated;
          }
        }

        const transKey = `categories.moderation.context.${cleanToken}`;
        const translatedToken = t(transKey);
        if (translatedToken && translatedToken !== transKey) {
          return translatedToken;
        }
        return cleanToken
          .replace(/_/g, " ")
          .replace(/\b\w/g, c => c.toUpperCase());
      })
      .join(", ");
  }
  
  // Case 3: Single token
  const transKey = `categories.moderation.context.${context}`;
  const translated = t(transKey);
  if (translated && translated !== transKey) {
    return translated;
  }
  
  return context.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const getStatusLabel = (status, t) => {
  if (!status) return "";
  const labels = {
    pending: t("page.flagged.status.pending", "Pending"),
    under_review: t("page.flagged.status.under_review", "Under Review"),
    underReview: t("page.flagged.status.under_review", "Under Review"),
    closed: t("page.flagged.status.closed", "Closed"),
    resolved: t("page.flagged.status.resolved", "Resolved"),
    dismissed: t("page.flagged.status.dismissed", "Dismissed"),
    decision_made: t("page.flagged.status.decision_made", "Decision Made"),
    action_taken: t("page.flagged.status.action_taken", "Action Taken"),
    actionTaken: t("page.flagged.status.action_taken", "Action Taken"),
    
    // Decision actions
    dismiss: t("page.flagged.status.dismiss", "Dismiss"),
    warn: t("page.flagged.status.warn", "Warn (Issue Warning)"),
    restrict: t("page.flagged.status.restrict", "Restrict Chat (24h)"),
    suspend: t("page.flagged.status.suspend", "Suspend User"),
  };
  return labels[status] || status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const getStatusDisplay = (flag, t) => {
  if (flag.status === "decision_made") {
    return getStatusLabel(flag.actionTaken, t);
  }
  return getStatusLabel(flag.status, t);
};

const getStatusColor = (flag) => {
  const status = flag.status;
  if (status === "pending") return "warning";
  if (status === "under_review") return "info";
  if (status === "closed" || status === "resolved" || status === "dismissed") return "success";
  if (status === "decision_made") {
    const action = flag.actionTaken;
    if (action === "warn") return "warning";
    return "danger";
  }
  return "secondary";
};

function BehavioralQueue() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();

  const {
    data,
    totalItems,
    loading,
    error,
    page,
    limit,
    search,
    setSearch,
    handlePageChange,
    handleLimitChange,
    refresh
  } = useModerationQueue("behavioral");

  // Section collapse states
  const [collapsedSections, setCollapsedSections] = useState({
    critical: false,
    high: false,
    medium: false
  });

  const toggleSection = (sec) => {
    setCollapsedSections(prev => ({ ...prev, [sec]: !prev[sec] }));
  };

  // Partition flags in memory
  const criticalFlags = data.filter(f => f.severity === "critical");
  const highFlags = data.filter(f => f.severity === "high");
  const mediumFlags = data.filter(f => f.severity === "medium" || f.severity === "low" || !f.severity);

  const getPriorityColor = (priority) => {
    if (priority === "urgent") return "danger";
    if (priority === "high") return "warning";
    if (priority === "medium") return "info";
    return "secondary";
  };

  const getPriorityLabel = (priority) => {
    if (!priority) return t("categories.moderation.priority.medium", "MEDIUM");
    return t(`categories.moderation.priority.${priority.toLowerCase()}`, priority.toUpperCase());
  };



  // Render table helper
  const renderFlagTable = (flagsList) => {
    if (flagsList.length === 0) {
      return (
        <p className="text-center text-muted py-4 mb-0">
          {t("categories.moderation.noFlags", "No flags found in this severity level.")}
        </p>
      );
    }

    return (
      <div className="table-responsive">
        <style>{`
          .dense-table td, .dense-table th {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
          }
        `}</style>
        <Table className="table align-items-center table-flush mb-0 dense-table">
          <thead className="thead-light">
            <tr>
              <th>{t("table.headers.user", "User")}</th>
              <th>{t("table.headers.flagType", "Type")}</th>
              <th>{t("table.headers.context", "Context")}</th>
              <th>{t("table.headers.actionStatus", "Action Status")}</th>
              <th>{t("table.headers.priority", "Priority")}</th>
              <th>{t("table.headers.createdAt", "Created At")}</th>
              <th>{t("table.headers.action", "Action")}</th>
            </tr>
          </thead>
          <tbody>
            {flagsList.map((flag) => {
              const userObj = flag.userId;
              const userName = userObj ? `${userObj.firstName || ""} ${userObj.lastName || ""}` : t("categories.moderation.unknown", "Unknown User");
              const userPic = userObj?.profilePictureUrl
                ? `${constants.s3Bucket}/profile-pics/${userObj.profilePictureUrl}`
                : null;

              const highlighted =
                isHighlighted("dispute", flag._id) ||
                isHighlighted("flag", flag._id) ||
                isHighlighted("behavioral", flag._id) ||
                isHighlighted("admin_flagged", flag._id) ||
                isHighlighted("support_ticket", flag._id) ||
                (flag.contentId && (isHighlighted("flag", flag.contentId) || isHighlighted("dispute", flag.contentId) || isHighlighted("listing", flag.contentId))) ||
                (flag.disputeId && isHighlighted("dispute", flag.disputeId));

              return (
                <tr key={flag._id} className={highlighted ? "border-left-highlight" : "hover:bg-neutral"}>
                  <td>
                    <div className="d-flex align-items-center">
                      {userPic ? (
                        <Avatar src={userPic} sx={{ width: 30, height: 30 }} />
                      ) : (
                        <Avatar
                            {...helpers.stringAvatar(userName)}
                            sx={{ width: 30, height: 30, fontSize: 12 }}
                          />
                      )}
                      <span className="ml-2 font-weight-bold">{userName}</span>
                    </div>
                  </td>
                  <td>
                    <Badge color={flag.flagType === "dispute" ? "info" : "warning"} className="badge-pill text-uppercase" style={{ fontSize: "10px" }}>
                      {flag.flagType === "dispute" ? t("categories.moderation.disputeFlag", "Dispute") : t("categories.moderation.behavioral", "Behavioral")}
                    </Badge>
                  </td>
                  <td style={{ maxWidth: "220px" }}>
                    {flag.flagType === "dispute" ? (
                      <div className="d-flex align-items-start" style={{ gap: "6px" }}>
                        <i className="fa fa-file-alt text-info mt-1" style={{ fontSize: "11px", flexShrink: 0 }} />
                        <span
                          className="text-slate-700 text-sm"
                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "180px" }}
                          title={flag.context || ""}
                        >
                          {flag.context || t("categories.moderation.noContext", "No context")}
                        </span>
                      </div>
                    ) : (
                      <div className="d-flex align-items-start" style={{ gap: "6px" }}>
                        <i className="fa fa-comment-alt text-warning mt-1" style={{ fontSize: "11px", flexShrink: 0 }} />
                        <span
                          className="font-italic text-slate-800 text-sm"
                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "180px" }}
                          title={formatContext(flag.context, t)}
                        >
                          &ldquo;{formatContext(flag.context, t)}&rdquo;
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge color={getStatusColor(flag)} className="badge-pill">
                      {getStatusDisplay(flag, t)}
                    </Badge>
                  </td>
                  <td>
                    <Badge color={getPriorityColor(flag.priority)} className="badge-pill">
                      {getPriorityLabel(flag.priority)}
                    </Badge>
                  </td>
                  <td>
                    {helpers.getFormattedDate(flag.createdAt, t)}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/moderation/behavioral/${flag._id}/details`)}
                      title={t("categories.moderation.viewDetails", "View Details")}
                    >
                      <VisibilityIcon fontSize="small" color="primary" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <>
      <SimpleHeader name={t("sideNav.behavioralQueue", "Behavioral & Disputes")} parentName={t("sideNav.moderation", "Moderation")} />
      
      <Container className="mt--6" fluid>
        {/* Search header bar */}
        <Card className="shadow mb-4">
          <CardBody className="py-3">
            <div className="d-flex align-items-center justify-content-between">
              <div className="dataTables_filter d-flex align-items-center w-50">
                <label className="d-flex align-items-center mb-0 w-100">
                  <span className="text-sm font-medium text-slate-600 mr-2">
                    {t("table.search", "Search")}:
                  </span>
                  <div className="position-relative d-flex align-items-center w-100" style={{ maxWidth: "250px" }}>
                    <Input
                      className="form-control-sm"
                      placeholder={t("categories.moderation.searchUser", "Search by user...")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ paddingRight: "24px" }}
                    />
                    {search && (
                      <span
                        onClick={() => setSearch("")}
                        className="position-absolute cursor-pointer search-clear-btn"
                        style={{ right: "8px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}
                      >
                        <CloseIcon style={{ fontSize: "14px" }} />
                      </span>
                    )}
                  </div>
                </label>
              </div>
              <Button size="sm" color="neutral" onClick={refresh}>
                <i className="fa fa-sync mr-1" />
                {t("categories.moderation.refreshQueue", "Refresh Queue")}
              </Button>
            </div>
          </CardBody>
        </Card>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        )}

        {error && (
          <Alert color="danger" className="shadow border-0">
            {error}
            <div className="mt-3">
              <Button color="secondary" size="sm" onClick={refresh}>
                {t("common.retry", "Retry")}
              </Button>
            </div>
          </Alert>
        )}

        {!loading && !error && (
          <div>
            {/* Section 1: CRITICAL Section */}
            <Card className="shadow mb-3 border-0 overflow-hidden">
              <CardHeader
                className="bg-danger text-white py-3 cursor-pointer d-flex justify-content-between align-items-center"
                onClick={() => toggleSection("critical")}
              >
                <h4 className="mb-0 text-white font-weight-700">
                  {t("categories.moderation.criticalFlags", "CRITICAL SEVERITY QUEUE")}
                </h4>
                <div>
                  <Badge color="secondary" className="mr-3 text-danger font-weight-700">
                    {criticalFlags.length}
                  </Badge>
                  <i className={`fas fa-chevron-${collapsedSections.critical ? "down" : "up"}`} />
                </div>
              </CardHeader>
              {!collapsedSections.critical && renderFlagTable(criticalFlags)}
            </Card>

            {/* Section 2: HIGH Section */}
            <Card className="shadow mb-3 border-0 overflow-hidden">
              <CardHeader
                className="bg-orange text-white py-3 cursor-pointer d-flex justify-content-between align-items-center"
                onClick={() => toggleSection("high")}
              >
                <h4 className="mb-0 text-white font-weight-700">
                  {t("categories.moderation.highFlags", "HIGH SEVERITY QUEUE")}
                </h4>
                <div>
                  <Badge color="secondary" className="mr-3 text-orange font-weight-700">
                    {highFlags.length}
                  </Badge>
                  <i className={`fas fa-chevron-${collapsedSections.high ? "down" : "up"}`} />
                </div>
              </CardHeader>
              {!collapsedSections.high && renderFlagTable(highFlags)}
            </Card>

            {/* Section 3: MEDIUM Section */}
            <Card className="shadow mb-3 border-0 overflow-hidden">
              <CardHeader
                className="bg-info text-white py-3 cursor-pointer d-flex justify-content-between align-items-center"
                onClick={() => toggleSection("medium")}
              >
                <h4 className="mb-0 text-white font-weight-700">
                  {t("categories.moderation.mediumFlags", "MEDIUM SEVERITY QUEUE")}
                </h4>
                <div>
                  <Badge color="secondary" className="mr-3 text-info font-weight-700">
                    {mediumFlags.length}
                  </Badge>
                  <i className={`fas fa-chevron-${collapsedSections.medium ? "down" : "up"}`} />
                </div>
              </CardHeader>
              {!collapsedSections.medium && renderFlagTable(mediumFlags)}
            </Card>

            {/* Pagination controls */}
            {!loading && data.length > 0 && (
              <Card className="shadow border-0 pt-2 pb-4 mt-4">
                <div className="pagination-override-margin">
                  <style>{`
                    .pagination-override-margin .pagination-container {
                      margin-bottom: 0px !important;
                      margin-top: 0px !important;
                    }
                  `}</style>
                  <Pagination
                    totalRows={totalItems}
                    rowsPerPage={limit}
                    currentPage={page - 1}
                    onPageChange={(p) => handlePageChange(p + 1)}
                    onRowsPerPageChange={(l) => handleLimitChange(l)}
                    t={t}
                  />
                </div>
              </Card>
            )}
          </div>
        )}
      </Container>

    </>
  );
}

export default BehavioralQueue;
